import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import API from "../api/axiosConfig";
import { RYDER_COUNTRY_CODES, getCleanPhone } from "../utils/phoneCountryCodes";
import { REQUEST_STATUS_VIEW } from "../utils/ryderRequestStatus";
import AvatarCapture from "../components/AvatarCapture";
import DocumentCapture from "../components/DocumentCapture";
import { checkDocumentPhoto } from "../utils/documentOcr";
import { toast } from "react-toastify";
import {
  User, Mail, Lock, Phone, IdCard, Bike, ArrowRight, ArrowLeft,
  CheckCircle, Loader2, Upload, X, Building2, ChevronRight,
  AlertTriangle, Camera,
} from "lucide-react";

/* ── Vehicle options ── */
const VEHICLES = [
  { type: "bicicleta", emoji: "🚲", label: "Bicicleta", desc: "Entregas ligeras y rápidas en distancias cortas.", capacities: ["Hasta 10 kg", "10 – 20 kg"] },
  { type: "moto",      emoji: "🛵", label: "Moto",      desc: "Perfecta para la mayoría de domicilios: rápida y ágil.", capacities: ["Hasta 20 kg", "20 – 50 kg", "50 – 100 kg"] },
  { type: "carro",     emoji: "🚗", label: "Carro",     desc: "Mayor capacidad para pedidos voluminosos o múltiples envíos.", capacities: ["Hasta 100 kg", "100 – 300 kg", "300 – 500 kg"] },
  { type: "camion",    emoji: "🚛", label: "Camión",    desc: "Carga pesada, mudanzas o distribución de gran volumen.", capacities: ["Hasta 1 tonelada", "1 – 3 toneladas", "3 – 5 toneladas", "Más de 5 toneladas"] },
  { type: "otro",      emoji: "📦", label: "Otro",      desc: "Cualquier otro medio de transporte.", capacities: ["Hasta 20 kg", "20 – 50 kg", "50 – 100 kg"] },
];

const DOC_TYPES = [
  { value: "CC",        label: "Cédula de Ciudadanía" },
  { value: "CE",        label: "Cédula de Extranjería" },
  { value: "TI",        label: "Tarjeta de Identidad" },
  { value: "PASAPORTE", label: "Pasaporte" },
];

/* ── Animation variants ── */
const variants = {
  enter:  (dir) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit:   (dir) => ({ x: dir > 0 ? -60 : 60, opacity: 0 }),
};

/* ── Field component ── */
function Field({ label, error, children, hint }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.5)",
        textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
        {label}
      </label>
      {children}
      {hint && !error && <p style={{ fontSize: 11, color: "rgba(255,255,255,0.28)", marginTop: 5 }}>{hint}</p>}
      {error && <p style={{ fontSize: 11, color: "#f87171", marginTop: 5, fontWeight: 600 }}>{error}</p>}
    </div>
  );
}

const inputStyle = {
  width: "100%", boxSizing: "border-box",
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 12, padding: "12px 14px",
  color: "white", fontSize: 14, outline: "none",
  transition: "border-color 0.2s, box-shadow 0.2s",
  fontFamily: "inherit",
};

function Input({ icon: Icon, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      {Icon && (
        <Icon size={15} style={{
          position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
          color: focused ? "#FEC400" : "rgba(255,255,255,0.3)", transition: "color 0.2s",
        }} />
      )}
      <input
        {...props}
        style={{
          ...inputStyle,
          paddingLeft: Icon ? 40 : 14,
          borderColor: focused ? "rgba(254,196,0,0.45)" : "rgba(255,255,255,0.1)",
          boxShadow: focused ? "0 0 0 3px rgba(254,196,0,0.08)" : "none",
        }}
        onFocus={(e) => { setFocused(true); props.onFocus?.(e); }}
        onBlur={(e)  => { setFocused(false); props.onBlur?.(e); }}
      />
    </div>
  );
}

function Select({ icon: Icon, children, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      {Icon && (
        <Icon size={15} style={{
          position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
          color: focused ? "#FEC400" : "rgba(255,255,255,0.3)", zIndex: 1, transition: "color 0.2s",
        }} />
      )}
      <select
        {...props}
        style={{
          ...inputStyle,
          paddingLeft: Icon ? 40 : 14,
          appearance: "none", cursor: "pointer",
          borderColor: focused ? "rgba(254,196,0,0.45)" : "rgba(255,255,255,0.1)",
          boxShadow: focused ? "0 0 0 3px rgba(254,196,0,0.08)" : "none",
        }}
        onFocus={(e) => { setFocused(true); props.onFocus?.(e); }}
        onBlur={(e)  => { setFocused(false); props.onBlur?.(e); }}
      >
        {children}
      </select>
      <ChevronRight size={13} style={{
        position: "absolute", right: 12, top: "50%", transform: "translateY(-50%) rotate(90deg)",
        color: "rgba(255,255,255,0.3)", pointerEvents: "none",
      }} />
    </div>
  );
}

/* ── Step 1: Personal info ── */
function StepPersonal({ data, onChange, errors, onDocBlur }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Field label="Nombre completo" error={errors.name}>
        <Input icon={User} type="text" name="name" value={data.name} onChange={onChange}
          placeholder="Juan García" required />
      </Field>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))", gap: 14 }}>
        <Field label="Email" error={errors.email}>
          <Input icon={Mail} type="email" name="email" value={data.email} onChange={onChange}
            placeholder="correo@email.com" required />
        </Field>
        <Field label="Contraseña" error={errors.password}>
          <Input icon={Lock} type="password" name="password" value={data.password} onChange={onChange}
            placeholder="••••••••" required />
        </Field>
      </div>

      <Field label="Teléfono" error={errors.phone}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 150px), 1fr))", gap: 10 }}>
          <Select name="phoneCountryCode" value={data.phoneCountryCode} onChange={onChange}>
            {RYDER_COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.flag} {c.code} {c.name}</option>)}
          </Select>
          <Input icon={Phone} type="tel" name="phone" value={data.phone} onChange={onChange}
            placeholder="41612345678" required />
        </div>
      </Field>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))", gap: 14 }}>
        <Field label="Tipo de documento" error={errors.documentType}>
          <Select icon={IdCard} name="documentType" value={data.documentType} onChange={onChange}>
            {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </Select>
        </Field>
        <Field label="Número de documento" error={errors.documentNumber}>
          <Input type="text" name="documentNumber" value={data.documentNumber} onChange={onChange}
            onBlur={onDocBlur} placeholder="1234567890" required />
        </Field>
      </div>
    </div>
  );
}

/* ── Step 2: Vehicle ── */
function StepVehicle({ data, onChange, errors }) {
  const selected = VEHICLES.find(v => v.type === data.type);

  const selectType = (type) => {
    const v = VEHICLES.find(v => v.type === type);
    onChange({ target: { name: "vehicle.type",     value: type } });
    onChange({ target: { name: "vehicle.capacity", value: v?.capacities[0] ?? "" } });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      {/* Vehicle type grid */}
      <Field label="Tipo de vehículo" error={errors.vehicleType}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 88px), 1fr))", gap: 10, marginTop: 4 }}>
          {VEHICLES.map(v => {
            const active = data.type === v.type;
            return (
              <button key={v.type} type="button" onClick={() => selectType(v.type)}
                style={{
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                  padding: "14px 8px", borderRadius: 14, cursor: "pointer",
                  background: active ? "rgba(254,196,0,0.1)" : "rgba(255,255,255,0.04)",
                  border: active ? "1.5px solid rgba(254,196,0,0.6)" : "1.5px solid rgba(255,255,255,0.08)",
                  transition: "all 0.2s",
                  boxShadow: active ? "0 0 20px rgba(254,196,0,0.12)" : "none",
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)"; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; }}
              >
                <span style={{ fontSize: 26, lineHeight: 1 }}>{v.emoji}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: active ? "#FEC400" : "rgba(255,255,255,0.5)" }}>
                  {v.label}
                </span>
              </button>
            );
          })}
        </div>
        {selected && (
          <div style={{ marginTop: 10, padding: "10px 14px", background: "rgba(254,196,0,0.06)",
            border: "1px solid rgba(254,196,0,0.15)", borderRadius: 10 }}>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", margin: 0 }}>
              <strong style={{ color: "#FEC400" }}>{selected.label}:</strong> {selected.desc}
            </p>
          </div>
        )}
      </Field>

      {/* Capacity */}
      <Field label="Capacidad de carga"
        hint="El administrador asignará pedidos según tu capacidad declarada.">
        <Select name="vehicle.capacity" value={data.capacity} onChange={onChange}>
          {(selected?.capacities ?? []).map(c => <option key={c} value={c}>{c}</option>)}
        </Select>
      </Field>

      {/* Plate / Brand / Model */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 150px), 1fr))", gap: 14 }}>
        <Field label="Placa">
          <Input type="text" name="vehicle.plate" value={data.plate} onChange={onChange}
            placeholder="ABC123" />
        </Field>
        <Field label="Marca">
          <Input type="text" name="vehicle.brand" value={data.brand} onChange={onChange}
            placeholder="Yamaha" />
        </Field>
        <Field label="Modelo">
          <Input type="text" name="vehicle.model" value={data.model} onChange={onChange}
            placeholder="FZ 2023" />
        </Field>
      </div>
    </div>
  );
}

/* ── Step 3: Document + Company ── */
function StepDocs({ onDocPhoto, onAvatar, company, onChange, companies, errors }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <Field label="Foto de perfil" error={errors.avatar}
        hint="Los clientes verán esta foto para reconocerte al recibir su pedido.">
        <AvatarCapture dark onAccept={onAvatar} />
      </Field>

      {/* Doc upload */}
      <Field label="Foto del documento de identidad" error={errors.docPhoto}
        hint="Cédula o documento legal. Se verifica que el número coincida y se guarda de forma segura.">
        <DocumentCapture dark onAccept={onDocPhoto} />
      </Field>

      {/* Company */}
      <Field label="Empresa (opcional)"
        hint="Si vas a trabajar para una empresa específica, selecciónala aquí.">
        <Select icon={Building2} name="company" value={company} onChange={onChange}>
          <option value="">Plataforma general (sin empresa)</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
      </Field>

      {/* Info box */}
      <div style={{ display: "flex", gap: 12, padding: "14px 16px",
        background: "rgba(254,196,0,0.06)", border: "1px solid rgba(254,196,0,0.18)", borderRadius: 14 }}>
        <AlertTriangle size={15} style={{ color: "#FEC400", flexShrink: 0, marginTop: 1 }} />
        <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 12.5, lineHeight: 1.65, margin: 0 }}>
          Tu solicitud será revisada por el administrador. Recibirás una notificación cuando sea aprobada o rechazada.
          El proceso toma entre 24 y 48 horas hábiles.
        </p>
      </div>
    </div>
  );
}

/* ── Progress indicator ── */
const STEPS = [
  { num: 1, label: "Tus datos" },
  { num: 2, label: "Vehículo" },
  { num: 3, label: "Documentos" },
];

function StepProgress({ current }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0, marginBottom: 40 }}>
      {STEPS.map((s, i) => {
        const done   = s.num < current;
        const active = s.num === current;
        return (
          <React.Fragment key={s.num}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
              <div style={{
                width: 36, height: 36, borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontWeight: 800, fontSize: 14, transition: "all 0.3s",
                background: done ? "#FEC400" : active ? "rgba(254,196,0,0.15)" : "rgba(255,255,255,0.06)",
                border: done ? "none" : active ? "2px solid rgba(254,196,0,0.6)" : "2px solid rgba(255,255,255,0.1)",
                color: done ? "#0d0f14" : active ? "#FEC400" : "rgba(255,255,255,0.3)",
                boxShadow: active ? "0 0 20px rgba(254,196,0,0.2)" : "none",
              }}>
                {done ? <CheckCircle size={16} /> : s.num}
              </div>
              <span style={{ fontSize: 11, fontWeight: 600, color: active ? "#FEC400" : "rgba(255,255,255,0.3)",
                letterSpacing: "0.04em" }}>
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1, height: 1.5, margin: "0 8px", marginBottom: 22, maxWidth: 80,
                background: s.num < current ? "#FEC400" : "rgba(255,255,255,0.08)",
                transition: "background 0.3s" }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ── Main component ── */
export default function RegisterRyder() {
  const navigate = useNavigate();
  const [step, setStep]   = useState(1);
  const [dir,  setDir]    = useState(1);
  const [companies, setCompanies] = useState([]);
  const [existingRequest, setExistingRequest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors]   = useState({});
  const [docTaken, setDocTaken] = useState(false);

  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", phoneCountryCode: "+58", password: "",
    documentType: "CC", documentNumber: "", company: "",
    vehicle: { type: "moto", plate: "", brand: "", model: "", capacity: "Hasta 20 kg" },
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [docPhoto,   setDocPhoto]   = useState(null);

  useEffect(() => {
    API.get("/companies/public?hasOwnDelivery=true")
      .then(res => {
        const list = res.data?.data?.data ?? res.data?.data ?? res.data ?? [];
        setCompanies(Array.isArray(list) ? list : []);
      })
      .catch(() => {});

    try {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      if (user?.id) {
        API.get(`/ryders/user/${user.id}`)
          .then(res => setExistingRequest(res.data?.data?.ryder ?? res.data?.data ?? null))
          .catch(() => {});
      }
    } catch {}
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "documentNumber") setDocTaken(false);
    if (name.startsWith("vehicle.")) {
      const field = name.split(".")[1];
      setFormData(p => ({ ...p, vehicle: { ...p.vehicle, [field]: value } }));
    } else {
      setFormData(p => ({ ...p, [name]: value }));
    }
  };

  const checkDocument = async () => {
    const documentNumber = formData.documentNumber.trim();
    if (!documentNumber) return;
    try {
      const res = await API.get("/ryders/document-available", { params: { documentNumber } });
      const available = res.data?.data?.available ?? true;
      setDocTaken(!available);
      setErrors(prev => ({ ...prev, documentNumber: available ? undefined : "Esta cédula ya está registrada" }));
    } catch {}
  };

  const handleDocPhoto = (file) => setDocPhoto(file);

  const handleAvatar = (file) => setAvatarFile(file);

  const validateStep = (s) => {
    const errs = {};
    if (s === 1) {
      if (!formData.name.trim())           errs.name           = "Nombre requerido";
      if (!formData.email.trim())          errs.email          = "Email requerido";
      if (!formData.password || formData.password.length < 6) errs.password = "Mínimo 6 caracteres";
      const { clean, expected } = getCleanPhone(formData.phone, formData.phoneCountryCode);
      if (!clean)                          errs.phone          = "Teléfono requerido";
      else if (clean.length !== expected)  errs.phone          = `Debe tener ${expected} dígitos`;
      if (!formData.documentNumber.trim()) errs.documentNumber = "Número de documento requerido";
      else if (docTaken)                   errs.documentNumber = "Esta cédula ya está registrada";
    }
    if (s === 2) {
      if (!formData.vehicle.type) errs.vehicleType = "Selecciona un vehículo";
    }
    if (s === 3) {
      if (!docPhoto) errs.docPhoto = "Debes subir la foto de tu documento";
      if (!avatarFile) errs.avatar = "Debes subir una foto de perfil";
    }
    return errs;
  };

  const goNext = () => {
    const errs = validateStep(step);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setDir(1);
    setStep(s => s + 1);
  };

  const goBack = () => {
    setErrors({});
    setDir(-1);
    setStep(s => s - 1);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const errs = validateStep(3);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setLoading(true);
    try {
      const ocr = await checkDocumentPhoto(docPhoto, formData.documentNumber).catch(() => ({ ok: true }));
      if (!ocr.ok) { setErrors({ docPhoto: ocr.message }); return; }
      const data = new FormData();
      data.append("name",           formData.name);
      data.append("email",          formData.email);
      data.append("phone",          getCleanPhone(formData.phone, formData.phoneCountryCode).clean);
      data.append("phoneCountryCode", formData.phoneCountryCode);
      data.append("documentType",   formData.documentType);
      data.append("documentNumber", formData.documentNumber);
      data.append("password",       formData.password);
      data.append("company",        formData.company);
      data.append("vehicle",        JSON.stringify(formData.vehicle));
      data.append("documentPhoto",  docPhoto);
      data.append("avatar",         avatarFile);
      const res = await API.post("/ryders", data);
      toast.success(res.data.message || "¡Solicitud enviada! Te notificaremos pronto.");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al enviar la solicitud");
    } finally {
      setLoading(false);
    }
  };

  const canResend = existingRequest?.approvalStatus === "rechazado" && existingRequest.canResubmit;

  if (existingRequest && !canResend) {
    const view = REQUEST_STATUS_VIEW[existingRequest.approvalStatus] ?? REQUEST_STATUS_VIEW.pendiente;
    return (
      <div style={{ minHeight: "100vh", background: "#0d0f14", display: "flex", alignItems: "center",
        justifyContent: "center", padding: "80px 16px 48px" }}>
        <div style={{ maxWidth: 480, width: "100%", background: "#111318",
          border: "1px solid rgba(255,255,255,0.07)", borderRadius: 24, padding: 32, textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>{view.icon}</div>
          <h1 style={{ color: "white", fontWeight: 900, fontSize: 20, margin: "0 0 10px" }}>{view.title}</h1>
          <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 14, lineHeight: 1.6, margin: "0 0 16px" }}>{view.text}</p>
          {existingRequest.rejectionReason && (
            <div style={{ textAlign: "left", padding: "12px 14px", marginBottom: 24, borderRadius: 12,
              background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.25)" }}>
              <p style={{ color: "#f87171", fontSize: 12, fontWeight: 700, margin: "0 0 4px" }}>Motivo</p>
              <p style={{ color: "rgba(255,255,255,0.75)", fontSize: 13, lineHeight: 1.6, margin: 0, whiteSpace: "pre-line" }}>
                {existingRequest.rejectionReason}
              </p>
            </div>
          )}
          <button onClick={() => navigate("/")}
            style={{ background: "linear-gradient(135deg, #FEC400, #ffd33d)", border: "none", borderRadius: 12,
              padding: "12px 24px", color: "#0d0f14", fontWeight: 800, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: "100vh", background: "#0d0f14", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "flex-start",
      backgroundImage: "radial-gradient(circle at 20% 20%, rgba(254,196,0,0.04) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(254,196,0,0.03) 0%, transparent 50%)",
      padding: "80px 16px 48px",
    }}>

      {canResend && (
        <div style={{ width: "100%", maxWidth: 560, marginBottom: 16, padding: "12px 14px", borderRadius: 12,
          background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.25)" }}>
          <p style={{ color: "#f87171", fontSize: 13, fontWeight: 700, margin: "0 0 4px" }}>Tu solicitud anterior fue rechazada</p>
          <p style={{ color: "rgba(255,255,255,0.75)", fontSize: 13, lineHeight: 1.6, margin: 0, whiteSpace: "pre-line" }}>
            {existingRequest.rejectionReason}
          </p>
          <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, margin: "6px 0 0" }}>Corrige lo necesario y vuelve a enviar tu solicitud.</p>
        </div>
      )}

      {/* Back button */}
      <div style={{ width: "100%", maxWidth: 560, marginBottom: 24 }}>
        <button onClick={() => navigate(-1)}
          style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none",
            color: "rgba(255,255,255,0.4)", cursor: "pointer", fontSize: 13, fontWeight: 600,
            padding: 0, transition: "color 0.2s" }}
          onMouseEnter={e => e.currentTarget.style.color = "rgba(255,255,255,0.75)"}
          onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.4)"}>
          <ArrowLeft size={16} /> Volver
        </button>
      </div>

      {/* Card */}
      <div style={{ width: "100%", maxWidth: 560,
        background: "#111318", border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: 24, overflow: "hidden",
        boxShadow: "0 24px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)" }}>

        {/* Amber top stripe */}
        <div style={{ height: 3, background: "linear-gradient(90deg, #c99a00, #FEC400, #fff246, #FEC400, #c99a00)" }} />

        {/* Header */}
        <div style={{ padding: "clamp(20px, 5vw, 32px) clamp(16px, 5vw, 32px) 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 6 }}>
            <div style={{ width: 48, height: 48, borderRadius: 14,
              background: "rgba(254,196,0,0.1)", border: "1px solid rgba(254,196,0,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>
              🛵
            </div>
            <div>
              <h1 style={{ color: "white", fontWeight: 900, fontSize: 20, margin: 0, letterSpacing: "-0.3px" }}>
                Únete como Domiciliario
              </h1>
              <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 13, margin: 0 }}>
                Gana por cada entrega, sin horarios fijos
              </p>
            </div>
          </div>

        </div>

        {/* Step progress */}
        <div style={{ padding: "clamp(18px, 5vw, 28px) clamp(16px, 5vw, 32px) 0" }}>
          <StepProgress current={step} />
        </div>

        {/* Step title */}
        <div style={{ padding: "0 clamp(16px, 5vw, 32px) 20px" }}>
          <h2 style={{ color: "white", fontWeight: 800, fontSize: 16, margin: 0 }}>
            {step === 1 && "Información personal"}
            {step === 2 && "Tu vehículo de trabajo"}
            {step === 3 && "Documentos y confirmación"}
          </h2>
          <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 13, margin: "4px 0 0" }}>
            {step === 1 && "Datos básicos para crear tu cuenta"}
            {step === 2 && "Con qué haces las entregas"}
            {step === 3 && "Último paso antes de enviar"}
          </p>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: "rgba(255,255,255,0.05)", margin: "0 32px" }} />

        {/* Step content */}
        <div style={{ padding: "24px clamp(16px, 5vw, 32px) 0", overflow: "hidden" }}>
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={step}
              custom={dir}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ type: "spring", damping: 28, stiffness: 320 }}
            >
              {step === 1 && (
                <StepPersonal data={formData} onChange={handleChange} errors={errors} onDocBlur={checkDocument} />
              )}
              {step === 2 && (
                <StepVehicle data={formData.vehicle} onChange={handleChange} errors={errors} />
              )}
              {step === 3 && (
                <StepDocs
                  onDocPhoto={handleDocPhoto}
                  onAvatar={handleAvatar}
                  company={formData.company} onChange={handleChange}
                  companies={companies} errors={errors}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer actions */}
        <div style={{ padding: "24px clamp(16px, 5vw, 32px) clamp(20px, 5vw, 32px)", display: "flex", flexWrap: "wrap", gap: 12, marginTop: 8 }}>
          {step > 1 && (
            <button type="button" onClick={goBack}
              style={{ display: "flex", alignItems: "center", gap: 6,
                background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 12, padding: "13px 20px", color: "rgba(255,255,255,0.6)",
                fontWeight: 700, fontSize: 14, cursor: "pointer", transition: "all 0.2s",
                fontFamily: "inherit" }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; e.currentTarget.style.color = "white"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.color = "rgba(255,255,255,0.6)"; }}>
              <ArrowLeft size={16} /> Atrás
            </button>
          )}

          {step < 3 ? (
            <button type="button" onClick={goNext}
              style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                background: "linear-gradient(135deg, #FEC400, #ffd33d)",
                border: "none", borderRadius: 12, padding: "13px 20px",
                color: "#0d0f14", fontWeight: 800, fontSize: 14, cursor: "pointer",
                boxShadow: "0 6px 24px rgba(254,196,0,0.28)", transition: "all 0.2s",
                fontFamily: "inherit" }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 10px 32px rgba(254,196,0,0.38)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 6px 24px rgba(254,196,0,0.28)"; }}>
              Siguiente <ArrowRight size={16} />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                background: loading ? "rgba(255,255,255,0.08)" : "linear-gradient(135deg, #FEC400, #ffd33d)",
                border: "none", borderRadius: 12, padding: "13px 20px",
                color: loading ? "rgba(255,255,255,0.3)" : "#0d0f14",
                fontWeight: 800, fontSize: 14,
                cursor: loading ? "not-allowed" : "pointer",
                boxShadow: loading ? "none" : "0 6px 24px rgba(254,196,0,0.28)",
                transition: "all 0.2s", fontFamily: "inherit" }}>
              {loading ? (
                <><Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> Enviando solicitud...</>
              ) : (
                <><CheckCircle size={16} /> Enviar solicitud</>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Benefits row */}
      <div style={{ width: "100%", maxWidth: 560, marginTop: 28,
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 150px), 1fr))", gap: 12 }}>
        {[
          { icon: "💰", label: "Gana por entrega", sub: "Sin límite de ingresos" },
          { icon: "🕐", label: "Tú decides cuándo", sub: "Sin horarios fijos" },
          { icon: "📲", label: "App incluida", sub: "Gestiona desde el móvil" },
        ].map(b => (
          <div key={b.label} style={{ background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14,
            padding: "14px 12px", textAlign: "center" }}>
            <div style={{ fontSize: 22, marginBottom: 6 }}>{b.icon}</div>
            <p style={{ color: "rgba(255,255,255,0.6)", fontWeight: 700, fontSize: 12, margin: "0 0 3px" }}>{b.label}</p>
            <p style={{ color: "rgba(255,255,255,0.25)", fontSize: 11, margin: 0 }}>{b.sub}</p>
          </div>
        ))}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
